import { useEffect, useRef } from 'react'
import { useReducedMotion } from '../../../hooks/useReducedMotion'
import candlefishIcon from '../../../assets/Greg.svg'
import { logoScene } from './logoScene'

const SIZE = 184
const FISH_WIDTH = 150
const FISH_HEIGHT = 75
const FRAME_MS = 1000 / 30
const clamp = (n: number, low: number, high: number) => Math.max(low, Math.min(high, n))
const angleDelta = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b))
const ease = (t: number) => { const p = clamp(t, 0, 1); return p * p * (3 - 2 * p) }

export default function SwimmingFish() {
  const fishRef = useRef<HTMLCanvasElement>(null)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const canvas = fishRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || prefersReducedMotion) return

    const image = new Image()
    let loaded = false
    let disposed = false
    let dpr = 1
    let width = window.innerWidth
    let height = window.innerHeight
    let protectedRects: DOMRect[] = []
    let top = 90
    let bottom = height - 150
    let x = -32
    let y = height * 0.3
    let heading = 0
    let turnRate = 0
    let speed = 34
    let beat = 0
    let clock = 0
    let started = false
    let frameId = 0
    let last = performance.now()
    const mounted = last
    let lastLayout = -Infinity
    let nextWaypoint = 0
    let nextCuriosity = 5
    let curiosityUntil = 0
    let curiousX = 0
    let curiousY = 0
    let goalX = width * 0.65
    let goalY = y
    let strokeTime = 0
    let strokePeriod = 9 + Math.random() * 4
    const phase = Math.random() * Math.PI * 2
    const pointer = { x: 0, y: 0, active: false }

    const measure = () => {
      width = window.innerWidth
      height = window.innerHeight
      const header = document.getElementById('navigation')?.getBoundingClientRect()
      const footer = document.querySelector('footer')?.getBoundingClientRect()
      top = Math.min(height - 40, Math.max(40, (header?.bottom ?? 48) + 34))
      bottom = Math.max(top + 1, Math.min(height - 35, (footer?.top ?? height) - 38))
      protectedRects = Array.from(document.querySelectorAll('#navigation, footer, [role="dialog"]'))
        .map(el => el.getBoundingClientRect())
        .filter(rect => rect.width > 0 && rect.height > 0)
    }
    const resize = () => {
      measure()
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(SIZE * dpr)
      canvas.height = Math.round(SIZE * dpr)
      if (started) {
        x = clamp(x, 24, Math.max(24, width - 24))
        y = clamp(y, top, bottom)
      }
      nextWaypoint = 0
    }
    const chooseWaypoint = () => {
      // Broad, unhurried traversals occasionally carry Greg behind the logo.
      goalX = width * (x < width / 2 ? 0.62 + Math.random() * 0.25 : 0.13 + Math.random() * 0.25)
      goalY = top + (bottom - top) * (0.15 + Math.random() * 0.7)
      nextWaypoint = clock + Math.hypot(goalX - x, goalY - y) / 27 + 8
    }
    const paint = (depth: number, opacity: number) => {
      const left = x - SIZE / 2
      const upper = y - SIZE / 2
      canvas.style.transform = `translate3d(${left}px, ${upper}px, 0)`
      canvas.style.opacity = String(opacity)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, SIZE, SIZE)
      ctx.save()
      ctx.translate(SIZE / 2, SIZE / 2)
      ctx.rotate(heading + Math.PI + Math.sin(beat) * (0.018 + speed * 0.00045))
      ctx.scale(depth, depth)
      ctx.drawImage(image, -FISH_WIDTH / 2, -FISH_HEIGHT / 2, FISH_WIDTH, FISH_HEIGHT)
      ctx.restore()

      // Erase only Greg's pixels. The live mask follows the logo's perspective,
      // bob and tail wave, leaving the background and both SVG assets intact.
      ctx.globalCompositeOperation = 'destination-out'
      const logo = logoScene.current
      if (logo && left < logo.left + logo.width && left + SIZE > logo.left &&
          upper < logo.top + logo.height && upper + SIZE > logo.top) {
        ctx.drawImage(logo.canvas, logo.left - left, logo.top - upper, logo.width, logo.height)
      }
      // A layout change or open modal must never let Greg obscure readable copy.
      for (const rect of protectedRects) {
        ctx.fillRect(rect.left - left - 6, rect.top - upper - 6, rect.width + 12, rect.height + 12)
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    const tick = (now: number) => {
      frameId = requestAnimationFrame(tick)
      if (now - last < FRAME_MS) return
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      if (!loaded) return
      const logo = logoScene.current
      if (!started) {
        // Start after the central entrance, with a fallback if that image fails.
        if (now < (logo?.readyAt ? logo.readyAt + 180 : mounted + 1500)) return
        measure()
        y = top + (bottom - top) * 0.3
        started = true
      }
      clock += dt
      if (now - lastLayout > 500) { measure(); lastLayout = now }
      if (clock > nextWaypoint || Math.hypot(goalX - x, goalY - y) < 40) chooseWaypoint()

      if (clock > nextCuriosity && pointer.active) {
        const distance = Math.hypot(pointer.x - x, pointer.y - y)
        if (distance > 55 && distance < 180 && pointer.y > top && pointer.y < bottom) {
          curiousX = clamp(pointer.x, 35, Math.max(35, width - 35))
          curiousY = clamp(pointer.y, top, bottom)
          curiosityUntil = clock + 1.6
          nextCuriosity = clock + 14 + Math.random() * 8
        }
      }
      const curious = clock < curiosityUntil
      const targetX = curious ? curiousX : goalX
      const targetY = curious ? curiousY : goalY
      const desired = Math.atan2(targetY - y, targetX - x)
      let vx = Math.cos(desired)
      let vy = Math.sin(desired)
      const margin = Math.min(85, width * 0.18, Math.max(20, (bottom - top) * 0.3))
      vx += clamp((margin + 24 - x) / margin, 0, 1) * 3
      vx -= clamp((x - (width - 24 - margin)) / margin, 0, 1) * 3
      vy += clamp((top + margin - y) / margin, 0, 1) * 3
      vy -= clamp((y - (bottom - margin)) / margin, 0, 1) * 3
      const wander = Math.sin(clock * 0.45 + phase) * 0.12
      const targetTurn = clamp(angleDelta(Math.atan2(vy, vx) + wander, heading) * 1.8, -0.9, 0.9)
      turnRate += (targetTurn - turnRate) * (1 - Math.exp(-dt * 3))
      heading += turnRate * dt

      // A short stroke followed by a long glide, with no instantaneous speed jump.
      strokeTime += dt
      if (strokeTime > strokePeriod) { strokeTime = 0; strokePeriod = 9 + Math.random() * 4 }
      const stroke = Math.exp(-strokeTime * 2.5)
      const targetSpeed = (27 + 28 * stroke) * (curious ? 0.82 : 1)
      speed += (targetSpeed - speed) * (1 - Math.exp(-dt * 2.5))
      beat += dt * (3.2 + speed * 0.055)
      x += Math.cos(heading) * speed * dt
      y += Math.sin(heading) * speed * dt
      const depthWave = Math.sin(clock * 0.19 + phase)
      const depth = 0.96 + 0.045 * depthWave
      const opacity = (0.8 + 0.08 * depthWave) * ease(clock / 0.8)
      paint(depth, opacity)
    }
    const onPointer = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      pointer.x = event.clientX
      pointer.y = event.clientY
      pointer.active = true
    }
    const onLeave = () => { pointer.active = false; curiosityUntil = 0 }
    const onLayout = () => { lastLayout = -Infinity }
    const onVisibility = () => {
      cancelAnimationFrame(frameId)
      if (!document.hidden) { last = performance.now(); frameId = requestAnimationFrame(tick) }
    }
    image.onload = () => { if (!disposed) loaded = true }
    image.src = candlefishIcon
    resize()
    window.addEventListener('resize', resize)
    window.addEventListener('scroll', onLayout, { passive: true })
    window.addEventListener('pointermove', onPointer, { passive: true })
    document.documentElement.addEventListener('pointerleave', onLeave)
    document.addEventListener('visibilitychange', onVisibility)
    if (!document.hidden) frameId = requestAnimationFrame(tick)

    return () => {
      disposed = true
      image.onload = null
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', resize)
      window.removeEventListener('scroll', onLayout)
      window.removeEventListener('pointermove', onPointer)
      document.documentElement.removeEventListener('pointerleave', onLeave)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [prefersReducedMotion])

  if (prefersReducedMotion) return null

  return (
    <canvas
      ref={fishRef}
      data-greg=""
      aria-hidden="true"
      className="fixed top-0 left-0 pointer-events-none -z-10 opacity-0"
      style={{ width: SIZE, height: SIZE }}
    />
  )
}
